'use client';

import {
  PaginationButtonSlot,
  PaginationEllipsisSlot,
  PaginationNextButtonSlot,
  PaginationPrevButtonSlot,
} from '@magicschool/spellbook/components/Pagination/slots';
import type { DynamicTextProp } from '@magicschool/spellbook/hooks/useDynamicTextResolver';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type React from 'react';

export type PaginationText = {
  /** Text for the "Go to page" button, used for accessibility. Accepts a static string or a function that receives the page number and returns a formatted string. */
  goToPage?: DynamicTextProp<[page: number]>;
  /** Text for the "Go to next page" button, used for accessibility. */
  goToNextPage?: string;
  /** Text for the "Go to previous page" button, used for accessibility. */
  goToPrevPage?: string;
};

export type PaginationProps = React.ComponentProps<'nav'> & {
  /** Whether or not the pagination is loading. If `true`, the buttons will show a loading spinner. Alternatively, you can use the `isLoading` prop on the individual buttons. */
  isLoading?: boolean;
  /** Whether or not the pagination buttons are disabled. If `true`, the buttons will be disabled and not clickable. Alternatively, you can use the `disabled` prop on the individual buttons. */
  disabled?: boolean;
  /** Callback function that is called when a page button is clicked. It does _not_ receive any arguments or check if the page is valid at the moment, so you should handle that logic in your implementation. */
  onPageChange?: () => void;
  /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
  text?: PaginationText;
};

const PaginationBase: React.FC<PaginationProps> = ({
  children,
  className,
  isLoading = false,
  disabled = false,
  onPageChange,
  text,
  ...props
}) => {
  const { orderedSlots } = useOrderedSlots(children, {
    button: { Component: PaginationButtonSlot, props: { isLoading, disabled, onPageChange, text } },
    prevButton: { Component: PaginationPrevButtonSlot, props: { isLoading, disabled, text } },
    nextButton: { Component: PaginationNextButtonSlot, props: { isLoading, disabled, text } },
    ellipsis: { Component: PaginationEllipsisSlot, props: {} },
  });

  /**
   * @TODO [SPE-70] Add Pagination left/right keyboard navigation support
   * https://linear.app/magicschool/issue/SPE-70/add-pagination-leftright-keyboard-navigation-support
   */
  return (
    <nav
      data-spellbook-name="pagination"
      role="navigation"
      aria-label="Pagination"
      className={cn('flex items-center justify-between', className)}
      {...props}
    >
      <ul className="flex items-center justify-center flex-wrap space-x-1">
        {orderedSlots.map((slot) => (
          <li key={`item-${slot.originalIndex}`} className="flex items-center">
            {slot.component}
          </li>
        ))}
      </ul>
    </nav>
  );
};

/**
 * Fully controlled pagination navigation for paged content. Renders a `<nav>` with an ordered list of slot-based page buttons, prev/next controls, and ellipsis indicators. All page state and navigation logic must be managed externally.
 *
 * ### When to use
 * - Paginating discrete, finite datasets (tables, search results, lists)
 * - When users need to jump to specific pages, not just scroll through content
 * - When the total page count is known and can be communicated to the user
 *
 * ### When NOT to use
 * - For infinite or streaming content: use infinite scroll or a "Load more" pattern instead
 * - When pagination is built into another component: `DataGrid` handles pagination internally via `DataGridPaginationState`
 * - For stepping through a linear flow (wizard/stepper): use a stepper component instead
 *
 * ### API
 * - `isLoading`: Propagates loading state to all child buttons. Individual buttons can also set their own `isLoading`.
 * - `disabled`: Propagates disabled state to all child buttons. Individual buttons can also set their own `disabled`.
 * - `onPageChange`: Fires when any page button is clicked. Receives no arguments — derive the target page from your own state.
 * - `text`: Accessible labels for screen readers. Accepts `goToPage` (string or function), `goToNextPage`, and `goToPrevPage`.
 *
 * ### Composition (slots)
 * - `<Pagination.Button>`: Represents an individual page. Required prop: `page` (number). The visible label defaults to `page` when no `children` are provided; pass `children` to override it. Set `isActive` on the current page. At least 2 are required.
 * - `<Pagination.PrevButton>`: Navigates to the previous page. Pass `disabled` when on the first page. Supports `iconOnly` for compact layouts.
 * - `<Pagination.NextButton>`: Navigates to the next page. Pass `disabled` when on the last page. Supports `iconOnly` for compact layouts.
 * - `<Pagination.Ellipsis>`: Indicates skipped page numbers between boundaries. Non-interactive.
 *
 * ### Visual states
 * - **Active page**: Renders with a border via `data-state="on"` and sets `aria-current="page"`
 * - **Loading**: Shows a spinner in place of content and sets `aria-busy="true"`
 * - **Disabled**: Visually muted, sets `aria-disabled="true"` (remains focusable for screen readers)
 *
 * ### Accessibility
 * - Renders as `<nav role="navigation" aria-label="Pagination">` by default
 * - Active page button sets `aria-current="page"`
 * - Disabled buttons use `aria-disabled` (not the `disabled` attribute) so they remain focusable for screen readers
 * - Loading buttons set `aria-busy="true"`
 * - Configure the `text` prop to provide localized screen reader labels for all navigation buttons
 */
export const Pagination = Object.assign(PaginationBase, {
  Button: PaginationButtonSlot,
  PrevButton: PaginationPrevButtonSlot,
  NextButton: PaginationNextButtonSlot,
  Ellipsis: PaginationEllipsisSlot,
});

/**
 * @TODO [SPE-8] Fix `displayName` assignment
 * https://linear.app/magicschool/issue/SPE-8/fix-displayname-assignment
 */
Pagination.displayName = 'Pagination';
